import type { CoverLetter } from '../types';

/**
 * Conversions de texte des lettres de motivation — fonctions PURES.
 *
 * Hors du service : `services/coverLetter` ouvre un client Supabase au chargement du module, si
 * bien qu'un test qui voulait juste convertir un texte devait fournir les variables d'environnement
 * du projet. Une fonction sans effet de bord ne doit rien exiger de tel.
 */

/**
 * Nombre de mots du corps : seuls comptent les jetons portant une lettre ou un chiffre. Même règle
 * que le serveur (`compterMots`) : une puce isolée ou un tiret ne sont pas des mots.
 */
export function compterMotsLettre(paragraphes: string[]): number {
  return paragraphes
    .join(' ')
    .split(/\s+/)
    .filter((jeton) => /[\p{L}\p{N}]/u.test(jeton)).length;
}

/** Même règle que le serveur (handler.interligne) : le rendu PDF doit se comporter pareil. */
export function interligneDe(mots: number): number {
  return mots <= 300 ? 1.5 : mots <= 330 ? 1.3 : 1.15;
}

/**
 * Transforme une lettre COLLÉE en lettre enregistrable.
 *
 * L'import crée la lettre, comme l'import d'un CV remplit « Mes CV » : une unité de quota dépensée
 * doit produire un artefact conservé, et son analyse avec. Sans ligne dans `cover_letters`, il n'y
 * aurait rien à quoi rattacher le diagnostic — et le § 6 de la politique promet qu'il est
 * « conservé avec la lettre à laquelle il se rapporte ».
 *
 * Le texte collé n'a pas de structure : on le découpe en paragraphes sur les lignes vides, et les
 * champs que seule une génération connaît (destinataire, coordonnées) restent vides. La lettre est
 * ainsi relisible dans « Mes lettres » sans prétendre à une structure qu'elle n'a pas.
 */
export function lettreDepuisTexte(
  texte: string,
  titre: string,
  texteReconstitue = false,
): CoverLetter {
  const paragraphes = texte
    .split(/\n\s*\n/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const mots = compterMotsLettre(paragraphes);

  return {
    objet: titre,
    candidat: { nom_complet: '', ville_ou_mobilite: '', telephone: '', email: '' },
    destinataire: { organisation: '', service_ou_fondation: '', nom: '', fonction: '', ville: '' },
    lieu_date: '',
    lettre: {
      formule_appel: '',
      // Une lettre d'un seul bloc reste un paragraphe : mieux vaut la conserver telle quelle que
      // d'inventer un découpage que l'auteur n'a pas fait.
      paragraphes: paragraphes.length > 0 ? paragraphes : [texte.trim()].filter(Boolean),
      formule_politesse: '',
      signature: '',
    },
    email_accompagnement: null,
    informations_a_confirmer: [],
    nombre_mots: mots,
    ...(texteReconstitue ? { texte_reconstitue: true } : {}),
    interligne: interligneDe(mots),
  };
}

/** Texte brut d'une lettre enregistrée, pour l'envoyer à l'analyse. */
export function texteDeLettre(letter: CoverLetter): string {
  return [
    letter.lettre.formule_appel,
    ...letter.lettre.paragraphes,
    letter.lettre.formule_politesse,
    letter.lettre.signature,
  ]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join('\n\n');
}
